import React, { useState, useEffect } from 'react'

import {
  Button,
  Icon as ButtonIcon,
  WindowExpand,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string,
  func,
  bool,
  node,
  number,
  oneOfType,
  oneOf,
} from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'
import Tooltip from 'Pods/Shared/Tooltip'

import { NOOP } from 'Utils/stableDefaults'

let ReactMDE
let editorCommands
let Showdown
let converter

const MAX_EDITOR_HEIGHT = 800

const TOOLBAR_COMMANDS = [
  ['bold', 'italic'],
  ['quote'],
  ['unordered-list', 'ordered-list'],
]

const COMMAND_ICONS = {
  bold: 'bold',
  italic: 'italic',
  quote: 'quote-right',
  'unordered-list': 'list-ul',
  'ordered-list': 'list-ol',
}

const renderIcon = (commandName, useNewStyles) => (
  <Icon type={['fas', COMMAND_ICONS[commandName]]} notSpaced={useNewStyles} />
)

// react-mde 11 enables keyboard shortcuts for every command in this map, so
// limit it to the toolbar commands (Ctrl+K would otherwise insert a link).
function pickToolbarCommands(commandMap) {
  return Object.fromEntries(TOOLBAR_COMMANDS.flat().map(command => [command, commandMap[command]]))
}

function RichEditor({
  onChange,
  value = null,
  label = '',
  required = false,
  moreInfo = '',
  customMoreInfo = null,
  name = 'rich-editor',
  showError,
  errorText = '',
  onBlur = NOOP,
  useNewStyles = false,
  minHeight = 200,
  onExpand = null,
  labelClassName = '',
}) {
  const [isMDEMounted, setIsMDEMounted] = useState(false)
  const [isShowdownMounted, setIsShowdownMounted] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [selectedTab, setSelectedTab] = useState('write')
  const inputErrorClass = showError && errorText !== '' ? 'is-invalid' : ''
  const unableToRender = value === null

  useEffect(() => {
    if (!isMDEMounted) {
      importMde()
    }
  }, [isMDEMounted])

  useEffect(() => {
    if (!isShowdownMounted) {
      importShowdown()
    }
  }, [isShowdownMounted])

  useEffect(() => {
    if (isMDEMounted && isShowdownMounted && !isMounted) {
      initConverter()
      setIsMounted(true)
    }
  }, [isMDEMounted, isShowdownMounted, isMounted])

  function importMde() {
    import('react-mde')
      .then((mde) => {
        ReactMDE = mde.default
        editorCommands = pickToolbarCommands(mde.getDefaultCommandMap())
        setIsMDEMounted(true)
      })
      .catch((e) => {
        handleLoadError('react-mde', e)
      })
  }

  function importShowdown() {
    import('showdown')
      .then((showdown) => {
        Showdown = showdown
        setIsShowdownMounted(true)
      })
      .catch((e) => {
        handleLoadError('showdown', e)
      })
  }

  function initConverter() {
    converter = new Showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
    })
  }

  function handleLoadError(module, error) {
    window.Sentry.captureException(`Error loading module ${module}:`, { extra: error })
  }

  function handleChange(newValue) {
    return onChange(newValue)
  }

  if (!isMDEMounted || !isShowdownMounted || !isMounted || unableToRender) {
    return null
  }

  return (
    <div
      className={
        useNewStyles
          ? 'cgt-form-element-wrapper cgt-form-element-wrapper--small rich-editor-new-styles'
          : 'rich-editor form-group form-group-grey'
      }
    >
      <div
        className={useNewStyles ? 'cgt-form-element-header' : 'rich-editor__header'}
      >
        {label.length > 0 && (
          <label
            htmlFor={name}
            className={classnames(
              useNewStyles && 'cgt-form-element-header__title cgt-legacy-text-caption',
              labelClassName,
            )}
          >
            {label}
            {required && <b> *</b>}
          </label>
        )}
        {moreInfo !== '' && (
          <Tooltip
            tooltipClassName="tooltip--medium tooltip--spaced u-text-center"
            id="rich-editor-tooltip"
            className="u-block"
            content={moreInfo}
            randomiseID
          >
            <Icon type={['fas', 'question-circle']} color="grey" className="info-icon ml-5" />
          </Tooltip>
        )}
        {customMoreInfo}
      </div>

      <div
        className={classnames('rich-editor__wrapper',
          !useNewStyles && 'rich-editor__editor',
          inputErrorClass,
        )}
        onBlur={onBlur}
      >
        {useNewStyles && !!onExpand && (
          <Button
            type="ghost"
            size="tiny"
            variant="only-icon"
            onClick={onExpand}
            icon={<ButtonIcon svgComponent={WindowExpand} />}
            customClass="description__expand-button"
          />
        )}
        <ReactMDE
          onChange={handleChange}
          value={value}
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          commands={editorCommands}
          toolbarCommands={TOOLBAR_COMMANDS}
          generateMarkdownPreview={markdown => Promise.resolve(converter.makeHtml(markdown))}
          getIcon={commandName => renderIcon(commandName, useNewStyles)}
          childProps={{
            // react-mde 11 sets tabIndex -1 on toolbar buttons; keep them reachable by keyboard.
            commandButtons: { tabIndex: 0 },
            textArea: { style: { height: minHeight, minHeight, maxHeight: MAX_EDITOR_HEIGHT } },
          }}
          classes={{
            reactMde: classnames(
              useNewStyles && 'new-rich-editor',
              useNewStyles && showError && errorText !== '' && 'new-rich-editor--error',
            ),
          }}
        />
      </div>

      {showError && errorText !== '' && useNewStyles && (
        <p className="cgt-form-element-footer cgt-legacy-text-caption cgt-legacy-color-utility-danger-100">{errorText}</p>
      )}

      {showError && errorText !== '' && !useNewStyles && (
        <div className="error-message">
          <span className="text">{errorText}</span>
        </div>
      )}
    </div>
  )
}

RichEditor.propTypes = {
  label: string,
  required: bool,
  value: oneOfType([string, oneOf([null])]),
  name: string,
  onChange: func.isRequired,
  onBlur: func,
  showError: bool.isRequired,
  errorText: string,
  moreInfo: string,
  customMoreInfo: node,
  useNewStyles: bool,
  minHeight: number,
  onExpand: func,
  labelClassName: string,
}

export default RichEditor
